import { UnreadMessageCount } from "@sendbird/chat/groupChannel";

// Enhanced SendBird SDK instance type for better type safety
export interface SendBirdInstance {
  currentUser?: {
    userId: string;
    nickname: string;
    profileUrl?: string;
    connectionStatus?: string;
    lastSeenAt?: number;
  };
  
  // Unread count methods - SDK version compatibility
  getTotalUnreadMessageCount?: () => Promise<number>;
  GroupChannel?: {
    getTotalUnreadMessageCount?: () => Promise<number>;
    createMyGroupChannelListQuery?: () => GroupChannelListQuery;
    getChannel?: (channelUrl: string) => Promise<GroupChannel>;
  };
  groupChannel?: {
    getTotalUnreadMessageCount?: () => Promise<number>;
    createMyGroupChannelListQuery?: () => GroupChannelListQuery;
    getChannel?: (channelUrl: string) => Promise<GroupChannel>;
  };
  
  // Channel management
  createGroupChannelListQuery?: () => GroupChannelListQuery;
  getChannel?: (channelUrl: string) => Promise<GroupChannel>;
  
  // Event handlers - SDK version compatibility
  addChannelHandler?: (handlerId: string, handler: ChannelHandler) => void;
  addConnectionHandler?: (handlerId: string, handler: ConnectionHandler) => void;
  addUserEventHandler?: (handlerId: string, handler: UserEventHandler) => void;
  removeChannelHandler?: (handlerId: string) => void;
  removeConnectionHandler?: (handlerId: string) => void;
  removeUserEventHandler?: (handlerId: string) => void;
  
  // Legacy handler interfaces
  ChannelHandler?: {
    add?: (handlerId: string, handler: ChannelHandler) => void;
  };
  ConnectionHandler?: {
    add?: (handlerId: string, handler: ConnectionHandler) => void;
  };
}

export interface GroupChannel {
  url: string;
  name: string;
  unreadMessageCount: number;
  markAsRead?: () => Promise<void>;
}

export interface GroupChannelListQuery {
  limit: number;
  next: () => Promise<GroupChannel[]>;
}

export interface ChannelHandler {
  onMessageReceived?: (channel: GroupChannel, message: any) => void;
  onMessageUpdated?: (channel: GroupChannel, message: any) => void;
  onMessageDeleted?: (channel: GroupChannel, messageId: string) => void;
  onReadReceiptUpdated?: (channel: GroupChannel) => void;
  onChannelChanged?: (channel: GroupChannel) => void;
  onChannelDeleted?: (channelUrl: string, channelType: string) => void;
}

export interface ConnectionHandler {
  onReconnectStarted?: () => void;
  onReconnectSucceeded?: () => void;
  onReconnectFailed?: () => void;
}

export interface UserEventHandler {
  onTotalUnreadMessageCountChanged?: (unreadMessageCount: UnreadMessageCount) => void;
}

// SDK Compatibility utility functions
export class SendBirdSDKUtils {
  /**
   * Get total unread message count with SDK version compatibility
   */
  static async getTotalUnreadCount(sb: SendBirdInstance): Promise<number> {
    if (!sb || !sb.currentUser) {
      return 0;
    }

    try {
      // Try different SDK API versions
      if (sb.getTotalUnreadMessageCount) {
        return await sb.getTotalUnreadMessageCount();
      }
      
      if (sb.GroupChannel?.getTotalUnreadMessageCount) {
        return await sb.GroupChannel.getTotalUnreadMessageCount();
      }
      
      if (sb.groupChannel?.getTotalUnreadMessageCount) {
        return await sb.groupChannel.getTotalUnreadMessageCount();
      }
      
      // Fallback: manually calculate from channel list
      return await this.calculateUnreadCountFromChannels(sb);
    } catch (error) {
      // Silently handle error - non-critical operation
      return 0;
    }
  }

  /**
   * Get channel with SDK version compatibility
   */
  static async getChannel(sb: SendBirdInstance, channelUrl: string): Promise<GroupChannel | null> {
    if (!sb || !channelUrl) {
      return null;
    }

    try {
      if (sb.GroupChannel?.getChannel) {
        return await sb.GroupChannel.getChannel(channelUrl);
      }
      
      if (sb.getChannel) {
        return await sb.getChannel(channelUrl);
      }
      
      if (sb.groupChannel?.getChannel) {
        return await sb.groupChannel.getChannel(channelUrl);
      }
      
      return null;
    } catch (error) {
      // Silently handle error - channel might not exist
      return null;
    }
  }

  /**
   * Add channel handler with SDK version compatibility
   */
  static addChannelHandler(
    sb: SendBirdInstance, 
    handlerId: string, 
    handler: ChannelHandler
  ): boolean {
    try {
      if (sb.addChannelHandler) {
        sb.addChannelHandler(handlerId, handler);
        return true;
      }
      
      if (sb.ChannelHandler?.add) {
        sb.ChannelHandler.add(handlerId, handler);
        return true;
      }
      
      return false;
    } catch (error) {
      // Silently handle error - handler registration failed
      return false;
    }
  }

  /**
   * Add connection handler with SDK version compatibility
   */
  static addConnectionHandler(
    sb: SendBirdInstance,
    handlerId: string,
    handler: ConnectionHandler
  ): boolean {
    try {
      if (sb.addConnectionHandler) {
        sb.addConnectionHandler(handlerId, handler);
        return true;
      }
      
      if (sb.ConnectionHandler?.add) {
        sb.ConnectionHandler.add(handlerId, handler);
        return true;
      }
      
      return false;
    } catch (error) {
      // Silently handle error - handler registration failed
      return false;
    }
  }

  /**
   * Add user event handler with SDK version compatibility
   */
  static addUserEventHandler(
    sb: SendBirdInstance,
    handlerId: string,
    handler: UserEventHandler
  ): boolean {
    try {
      if (sb.addUserEventHandler) {
        sb.addUserEventHandler(handlerId, handler);
        return true;
      }
      
      return false;
    } catch (error) {
      // Silently handle error - handler registration failed
      return false;
    }
  }

  /**
   * Remove handlers with SDK version compatibility
   */
  static removeHandlers(sb: SendBirdInstance, handlerIds: string[]): void {
    handlerIds.forEach(handlerId => {
      try {
        if (sb.removeChannelHandler) {
          sb.removeChannelHandler(handlerId);
        }
        if (sb.removeConnectionHandler) {
          sb.removeConnectionHandler(handlerId);
        }
        if (sb.removeUserEventHandler) {
          sb.removeUserEventHandler(handlerId);
        }
      } catch (error) {
        // Silently handle error - handler might already be removed
      }
    });
  }

  /**
   * Check if SDK instance is valid and user is connected
   */
  static isValidInstance(sb: SendBirdInstance | undefined): sb is SendBirdInstance {
    return !!(sb && sb.currentUser);
  }

  /**
   * Mark channel as read with error handling
   */
  static async markChannelAsRead(sb: SendBirdInstance, channelUrl: string): Promise<boolean> {
    try {
      const channel = await this.getChannel(sb, channelUrl);
      if (channel?.markAsRead) {
        await channel.markAsRead();
        return true;
      }
      return false;
    } catch (error) {
      // Silently handle error - channel marking failed
      return false;
    }
  }

  /**
   * Fallback method to calculate unread count from channel list
   */
  private static async calculateUnreadCountFromChannels(sb: SendBirdInstance): Promise<number> {
    try {
      let query: GroupChannelListQuery | null = null;

      if (sb.createGroupChannelListQuery) {
        query = sb.createGroupChannelListQuery();
      } else if (sb.GroupChannel?.createMyGroupChannelListQuery) {
        query = sb.GroupChannel.createMyGroupChannelListQuery();
      } else if (sb.groupChannel?.createMyGroupChannelListQuery) {
        query = sb.groupChannel.createMyGroupChannelListQuery();
      }

      if (!query) {
        return 0;
      }

      query.limit = 100;
      const channels = await query.next();
      
      return channels.reduce((total, channel) => {
        return total + (channel.unreadMessageCount || 0);
      }, 0);
    } catch (error) {
      // Silently handle error - fallback calculation failed
      return 0;
    }
  }
}

// Error boundary helper for SendBird operations
export class SendBirdErrorHandler {
  static handleError(error: any, context: string): void {
    // Log error for debugging but don't throw to prevent app crashes
    // In production, this would log to an error reporting service
    // For now, silently handle to prevent console noise
    
    // You could add error reporting service here
    // reportError(error, { context: `sendbird_${context}` });
  }
  
  static async withErrorHandling<T>(
    operation: () => Promise<T>,
    context: string,
    fallbackValue: T
  ): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      this.handleError(error, context);
      return fallbackValue;
    }
  }
}